import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Upload, CheckCircle2, AlertTriangle, FileText, Trash2, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

function ImportTab({ kind }) {
  const { activeBranchId } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const doPreview = async () => {
    if (!file) return;
    setLoading(true); setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("branch_id", activeBranchId);
    try {
      const { data } = await api.post(`/import/${kind}/preview`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      toast.success(`Parsed ${data.total} rows`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const doCommit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/import/${kind}/commit`, { branch_id: activeBranchId, rows: preview.rows });
      setResult(data); setPreview(null); setFile(null);
      toast.success(`Imported: ${data.created}${data.skipped ? ` (${data.skipped} skipped)` : ""}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const sample = kind === "products"
    ? "name,barcode,sku,category,cost_price,margin_percent,stock,gst_percent,low_stock_alert"
    : kind === "customers"
    ? "name,phone,gender,address,email,date_of_birth (YYYY-MM-DD)"
    : "name,phone,gstin,email,address";

  return (
    <div className="space-y-4">
      <div className="text-xs bg-slate-100 border border-slate-200 p-2 font-mono">Columns: {sample}</div>
      <div className="flex gap-2">
        <Input type="file" accept=".csv,.xlsx" onChange={(e) => { setFile(e.target.files?.[0]); setPreview(null); setResult(null); }} className="rounded-none" data-testid={`import-file-${kind}`} />
        <Button onClick={doPreview} disabled={!file || loading} className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid={`preview-${kind}`}><Upload className="w-4 h-4 mr-2" />Preview</Button>
      </div>
      {preview && (
        <div className="space-y-3">
          <div className="flex gap-3 text-sm">
            <div className="flex items-center gap-1 text-green-700"><CheckCircle2 className="w-4 h-4" />{preview.total} valid rows</div>
            {preview.errors?.length > 0 && <div className="flex items-center gap-1 text-amber-700"><AlertTriangle className="w-4 h-4" />{preview.errors.length} errors</div>}
          </div>
          <div className="max-h-64 overflow-auto border border-slate-200">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 sticky top-0"><tr>{Object.keys(preview.rows[0] || {}).map((k) => <th key={k} className="p-2 text-left">{k}</th>)}</tr></thead>
              <tbody>{preview.rows.slice(0, 30).map((r, i) => (
                <tr key={i} className="border-b border-slate-100">{Object.values(r).map((v, j) => <td key={j} className="p-2">{String(v)}</td>)}</tr>
              ))}</tbody>
            </table>
          </div>
          {preview.errors?.length > 0 && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 p-2 max-h-32 overflow-auto">
              {preview.errors.map((e, i) => <div key={i}>Row {e.row}: {e.error}</div>)}
            </div>
          )}
          <Button onClick={doCommit} disabled={loading} className="bg-green-600 hover:bg-green-700 rounded-none" data-testid={`commit-${kind}`}>Commit Import</Button>
        </div>
      )}
      {result && <div className="bg-green-50 border border-green-200 p-3 text-sm text-green-800">Created: {result.created}{result.skipped ? `, Skipped duplicates: ${result.skipped}` : ""}</div>}
    </div>
  );
}


// ============================================================
// Tally Import — async background jobs
// Upload files → each becomes a separate job → poll /import-jobs page for progress.
// ============================================================
function TallyImport() {
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState([]);

  const handleFiles = async (fileList) => {
    const arr = Array.from(fileList || []);
    if (!arr.length) return;
    setUploading(true);
    const results = [];
    for (const f of arr) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/import/tally/v2/jobs", fd, { headers: { "Content-Type": "multipart/form-data" } });
        results.push({ file: f.name, status: "queued", job_id: data.job_id });
      } catch (e) {
        if (e.response?.status === 409) {
          results.push({ file: f.name, status: "duplicate", message: e.response.data?.detail });
        } else {
          results.push({ file: f.name, status: "error", message: formatApiError(e) });
        }
      }
    }
    setUploading(false);
    setUploadResults(results);
    const queued = results.filter((r) => r.status === "queued").length;
    if (queued > 0) toast.success(`${queued} job(s) queued — track progress on the Jobs page`);
  };

  const onDrop = (e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); };

  return (
    <div className="space-y-4" data-testid="tally-import">
      <div className="bg-indigo-50 border-l-4 border-indigo-400 p-3 text-xs text-slate-700">
        <div className="font-semibold mb-1 flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-indigo-600" />Tally ERP Full Migration — async background jobs</div>
        Drop ANY of the 10 Tally CSV exports. Each file becomes a separate background job — you can close this page and check progress on the <b>Tally Import Jobs</b> page anytime.
        <ul className="list-disc list-inside mt-1 space-y-0.5">
          <li>Purchase / Sales / Returns vouchers → parties, products, customers, historical bills</li>
          <li>Receipts / Payments → standalone receipts, expenses, party payments</li>
          <li>Stock Journal → stock adjustments · P&L → display summary · Contra → safely skipped</li>
        </ul>
        <div className="mt-1 text-[11px] text-slate-600">All migrated rows are tagged <span className="font-mono bg-slate-100 px-1">imported_from_tally=true</span>. Dedup is case-insensitive — safe to re-run. Identical pending/running uploads are refused.</div>
      </div>

      <label htmlFor="tally-files" onDragOver={(e) => e.preventDefault()} onDrop={onDrop}
        className="block border-2 border-dashed border-slate-300 hover:border-slate-500 bg-slate-50 hover:bg-slate-100 transition-colors p-8 text-center cursor-pointer rounded-sm"
        data-testid="tally-dropzone">
        {uploading ? <Loader2 className="w-8 h-8 mx-auto mb-2 text-slate-500 animate-spin" /> : <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />}
        <div className="text-sm font-medium text-slate-700">{uploading ? "Queuing jobs..." : "Drop Tally CSV files here, or click to select"}</div>
        <div className="text-xs text-slate-500 mt-1">Each file → 1 background job → track on the Jobs page.</div>
        <input id="tally-files" type="file" multiple accept=".csv" className="hidden"
          onChange={(e) => handleFiles(e.target.files)} data-testid="tally-file-input" />
      </label>

      {uploadResults.length > 0 && (
        <Card className="rounded-sm border-slate-200 p-3" data-testid="tally-upload-results">
          <div className="text-xs font-medium mb-2 flex items-center justify-between">
            <span>Last upload batch ({uploadResults.length} file{uploadResults.length !== 1 ? "s" : ""})</span>
            <Button size="sm" variant="outline" onClick={() => navigate("/import-jobs")} className="rounded-none h-7 text-xs" data-testid="goto-jobs">
              Track progress →
            </Button>
          </div>
          <div className="space-y-1 text-xs">
            {uploadResults.map((r, i) => (
              <div key={i} className="flex items-center gap-2">
                {r.status === "queued" && <span className="text-green-700">✓ queued</span>}
                {r.status === "duplicate" && <span className="text-amber-700">⚠ duplicate</span>}
                {r.status === "error" && <span className="text-red-700">✗ error</span>}
                <span className="font-mono text-slate-700">{r.file}</span>
                {r.message && <span className="text-slate-500">— {r.message}</span>}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value, muted }) {
  return (
    <div className={`border border-slate-200 bg-white p-2 ${muted ? "opacity-70" : ""}`} data-testid={`stat-${label.replace(/\s+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-lg font-bold tabular-nums">{value}</div>
    </div>
  );
}


export default function BulkImport() {
  return (
    <div className="space-y-6 max-w-4xl" data-testid="bulk-import-page">
      <Card className="rounded-sm p-6 border-slate-200">
        <h2 className="text-xl font-bold tracking-tight mb-1" style={{ fontFamily: "Cabinet Grotesk, IBM Plex Sans, sans-serif" }}>Bulk Import & Migration</h2>
        <p className="text-sm text-slate-500 mb-4">Upload generic CSV/XLSX templates or your Tally ERP voucher exports — system auto-detects and migrates master data.</p>
        <Tabs defaultValue="tally">
          <TabsList className="rounded-none">
            <TabsTrigger value="tally" className="rounded-none" data-testid="tab-tally"><Sparkles className="w-3.5 h-3.5 mr-1" />Tally ERP</TabsTrigger>
            <TabsTrigger value="products" className="rounded-none" data-testid="tab-products">Products</TabsTrigger>
            <TabsTrigger value="parties" className="rounded-none" data-testid="tab-parties">Parties</TabsTrigger>
            <TabsTrigger value="customers" className="rounded-none" data-testid="tab-customers">Customers</TabsTrigger>
          </TabsList>
          <TabsContent value="tally" className="mt-4"><TallyImport /></TabsContent>
          <TabsContent value="products" className="mt-4"><ImportTab kind="products" /></TabsContent>
          <TabsContent value="parties" className="mt-4"><ImportTab kind="parties" /></TabsContent>
          <TabsContent value="customers" className="mt-4"><ImportTab kind="customers" /></TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}
